import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardText,
  CCardTitle,
  CCol,
  CPagination,
  CPaginationItem,
  CRow,
  CSpinner,
  CTooltip,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle, cilChevronRight, cilCreditCard } from "@coreui/icons";
import {
  type CustomerTradingStrategySubscriptionDto,
  deriveSubscriptionDisplayStatus,
  type SubscriptionDisplayStatus,
  subscriptionStatusColor,
  isActiveSubscription,
  useCheckoutStatus,
  useMySubscriptions,
} from "../../api/hooks/useStrategySubscriptions";
import { routePaths } from "../../routes/route-paths";
import {
  clearPendingCheckout,
  readPendingCheckout,
} from "../../state/pendingCheckout";
import { StrategyIdentityBand } from "./components/StrategyIdentityBand";
import { StrategyCurve } from "./components/StrategyCurve";
import { StrategySideLabel } from "./components/StrategySideLabel";
import { bySide, countStrategies, groupSides, sideGroupKey } from "./strategySides";
import { useBillingSummary } from "../../api/hooks/useBilling";
import {
  type CustomerMembershipDto,
  useMyMemberships,
} from "../../api/hooks/useMemberships";
import { useMembershipsEnabled } from "../../api/hooks/usePortalSettings";
import {
  type TradingStrategyDto,
  useTradingStrategies,
} from "../../api/hooks/useTradingStrategies";
import { IncludedStrategyCard } from "./components/IncludedStrategyCard";
import { BacktestNote } from "./components/BacktestNote";
import { formatDateShort } from "../../lib/dates";
import { formatMembershipNames } from "../../lib/membership-copy";
import { MembershipBadge, MembershipBadgeStack } from "./components/MembershipBadge";
import { TradingViewMark } from "./components/TradingViewMark";
import { TRADINGVIEW_SCRIPTS_ADD_ON_TYPE, useAddOns } from "../../api/hooks/useAddOns";
import { useTradingViewScripts } from "../../api/hooks/useTradingViewScripts";

const PAGE_SIZE = 9;

// Two rows on a wide screen, so a membership of dozens of strategies doesn't bury the page.
const INCLUDED_PREVIEW_COUNT = 6;

// How long to keep waiting for a just-bought subscription to appear before giving up and pointing
// at support. Generous on purpose: the row is written at the end of a chain (checkout webhook ->
// Stripe subscription -> Stripe invoice -> invoice.paid webhook), not by the payment itself.
const POST_CHECKOUT_WAIT_TIMEOUT_MS = 90_000;

type StatusFilter = "all" | "active" | "inactive";

function isStatusFilter(value: string | null): value is StatusFilter {
  return value === "all" || value === "active" || value === "inactive";
}

export function MySubscriptionsCoreui() {
  const [searchParams, setSearchParams] = useSearchParams();
  const membershipsEnabled = useMembershipsEnabled();
  const navigate = useNavigate();

  // Left behind by the checkout wizard when payment completed: this is someone who has just paid,
  // and expectedCount is how many rows the list holds once their purchase has landed. Read once on
  // mount — absent for every other arrival, so a genuinely new customer still gets the ordinary
  // empty state rather than a wait that never ends.
  const [pendingCheckout] = useState(readPendingCheckout);
  const [waitExpired, setWaitExpired] = useState(false);

  // The marker asserts a payment; nothing until now checked that assertion against the server.
  // hasBillingAccount is false until a customer's first checkout creates their Stripe Customer, so a
  // marker sitting beside a customer who has none cannot describe a payment they made — it is left
  // over from a checkout whose rows are gone, or from a different customer in this tab. Believing it
  // tells someone who has never paid that their payment went through, and points them at support for
  // a problem that does not exist.
  //
  // Only asked for when there is a marker to check, and only trusted once it has loaded: until then
  // the wait stands, so a customer who really has just paid never flashes the empty state.
  const { data: billing } = useBillingSummary({
    enabled: pendingCheckout !== null,
  });
  const markerIsStale =
    pendingCheckout !== null &&
    billing !== undefined &&
    !billing.hasBillingAccount;

  const expectedCount = markerIsStale
    ? 0
    : (pendingCheckout?.expectedCount ?? 0);

  const {
    data: subscriptions = [],
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
  } = useMySubscriptions(
    expectedCount > 0 && !waitExpired ? expectedCount : undefined,
  );
  const memberships = useMyMemberships();
  const tradingStrategies = useTradingStrategies();
  const addOns = useAddOns();
  const holdsTradingViewScripts = (addOns.data ?? []).some(
    (addOn) => addOn.type === TRADINGVIEW_SCRIPTS_ADD_ON_TYPE && addOn.held !== null,
  );
  const tradingViewScripts = useTradingViewScripts({ enabled: holdsTradingViewScripts });
  const invitedAsByStrategyId = useMemo(
    () =>
      new Map(
        (tradingViewScripts.data ?? []).map((script) => [script.tradingStrategyId, script.tradingViewUsername]),
      ),
    [tradingViewScripts.data],
  );
  const membershipItems = useMemo(
    () => memberships.data ?? [],
    [memberships.data],
  );
  const isLoading =
    subscriptionsLoading || memberships.isLoading || tradingStrategies.isLoading;
  const error =
    subscriptionsError ?? memberships.error ?? tradingStrategies.error;

  const [membershipFilter, setMembershipFilter] = useState<string | null>(null);
  const [showAllIncluded, setShowAllIncluded] = useState(false);

  // Short of what was bought: the purchase is in flight rather than missing. Stays true once
  // expired so the panel can switch to the support message instead of dropping back to "you don't
  // have any subscriptions yet".
  const awaitingCheckout =
    expectedCount > 0 && subscriptions.length < expectedCount;

  // Asked while the wait stands: a declined card means what it couldn't pay for will never arrive.
  const checkoutStatus = useCheckoutStatus(
    awaitingCheckout ? (pendingCheckout?.sessionId ?? null) : null,
  );
  const checkoutDeclined = checkoutStatus.data?.status === "PaymentDeclined";

  // startedAt comes from the marker rather than this mount, so a refresh mid-wait resumes the
  // window where it left off instead of granting a fresh one.
  useEffect(() => {
    if (!awaitingCheckout || waitExpired || !pendingCheckout) return;
    const remaining = Math.max(
      0,
      POST_CHECKOUT_WAIT_TIMEOUT_MS - (Date.now() - pendingCheckout.startedAt),
    );
    const timer = setTimeout(() => setWaitExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [awaitingCheckout, waitExpired, pendingCheckout]);

  // Once they've all arrived — or once the marker turns out to be stale — drop it, so a later visit
  // in this tab is an ordinary one. Left in place on expiry alone, so a refresh mid-wait still shows
  // the support message rather than the empty state.
  useEffect(() => {
    if (!pendingCheckout || awaitingCheckout) return;
    clearPendingCheckout();
  }, [pendingCheckout, awaitingCheckout]);

  const statusFilter: StatusFilter = isStatusFilter(searchParams.get("status"))
    ? (searchParams.get("status") as StatusFilter)
    : "active";

  const included = useMemo(
    () =>
      includedStrategies(
        subscriptions,
        membershipItems,
        tradingStrategies.data ?? [],
      ),
    [subscriptions, membershipItems, tradingStrategies.data],
  );

  // Counted as cards: a strategy's sides share one card, so two held sides count once.
  const counts = useMemo(() => {
    const activeStrategies = subscriptions
      .filter((s) => isActiveSubscription(s))
      .map((s) => s.strategy);
    const inactiveStrategies = subscriptions
      .filter((s) => !isActiveSubscription(s))
      .map((s) => s.strategy);
    const allStrategies = subscriptions.map((s) => s.strategy);
    return {
      all: countStrategies(allStrategies) + included.length,
      active: countStrategies(activeStrategies) + included.length,
      inactive: countStrategies(inactiveStrategies),
    };
  }, [subscriptions, included]);

  const filtered = useMemo(() => {
    let list = subscriptions;
    if (statusFilter === "active") {
      list = list.filter((s) => isActiveSubscription(s));
    } else if (statusFilter === "inactive") {
      list = list.filter((s) => !isActiveSubscription(s));
    }
    return [...list].sort(
      (a, b) => +new Date(b.activeFrom) - +new Date(a.activeFrom),
    );
  }, [subscriptions, statusFilter]);

  const filteredGroups = useMemo(
    () => groupBySubscribedStrategy(filtered),
    [filtered],
  );

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
  const requestedPage = Math.max(
    1,
    parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );
  const currentPage = Math.min(requestedPage, totalPages);

  // Keep URL honest if filters shrink the result set or the user types in an
  // out-of-range page.
  useEffect(() => {
    if (requestedPage !== currentPage) {
      const next = new URLSearchParams(searchParams);
      if (currentPage <= 1) next.delete("page");
      else next.set("page", currentPage.toString());
      setSearchParams(next, { replace: true });
    }
  }, [requestedPage, currentPage, searchParams, setSearchParams]);

  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const visibleGroups = filteredGroups.slice(startIdx, startIdx + PAGE_SIZE);
  const endIdx = startIdx + visibleGroups.length;

  const updateStatus = (next: StatusFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === "active") {
      params.delete("status");
    } else {
      params.set("status", next);
    }
    params.delete("page");
    setSearchParams(params);
  };

  const goToPage = (page: number) => {
    const target = Math.min(Math.max(1, page), totalPages);
    if (target === currentPage) return;
    const params = new URLSearchParams(searchParams);
    if (target <= 1) params.delete("page");
    else params.set("page", target.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showPagination = totalPages > 1;

  // Inactive is past subscriptions only: an ended membership's strategies leave the page.
  const showIncluded = statusFilter !== "inactive" && included.length > 0;
  // A membership that ends while it is the one selected leaves nothing to select, so the filter lets go.
  const selectedMembershipId = membershipItems.some((m) => m.id === membershipFilter)
    ? membershipFilter
    : null;
  const filteredIncluded =
    selectedMembershipId === null
      ? included
      : included.filter((i) =>
          i.memberships.some((m) => m.id === selectedMembershipId),
        );
  const visibleIncluded = showAllIncluded
    ? filteredIncluded
    : filteredIncluded.slice(0, INCLUDED_PREVIEW_COUNT);

  return (
    <div>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h2 className="mb-0">My strategies</h2>
          <p className="text-body-secondary mb-0">
            {membershipsEnabled
              ? "What you subscribe to and what your memberships include."
              : "What you subscribe to."}
          </p>
        </div>
        <div className="col-12 col-sm-auto d-grid">
          {/* Billing is per-customer, not per-subscription — one card, one monthly invoice covering the
              whole bundle. So it gets its own page rather than a section on each subscription. */}
          <CButton
            color="primary"
            onClick={() => navigate(routePaths.billing)}
          >
            <CIcon icon={cilCreditCard} className="me-2" />
            Billing
          </CButton>
        </div>
      </div>

      {awaitingCheckout &&
        (checkoutDeclined ? (
          <CheckoutDeclinedPanel />
        ) : (
          <CheckoutWaitPanel expired={waitExpired} />
        ))}

      {(subscriptions.length > 0 || included.length > 0) && (
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <FilterPill
            label="Active"
            count={counts.active}
            active={statusFilter === "active"}
            onClick={() => updateStatus("active")}
          />
          <FilterPill
            label="Inactive"
            count={counts.inactive}
            active={statusFilter === "inactive"}
            onClick={() => updateStatus("inactive")}
          />
          <FilterPill
            label="All"
            count={counts.all}
            active={statusFilter === "all"}
            onClick={() => updateStatus("all")}
          />
        </div>
      )}

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      ) : error ? (
        <CAlert color="danger">
          Unable to load your strategies. Please try again.
        </CAlert>
      ) : subscriptions.length === 0 && included.length === 0 ? (
        // Suppressed while waiting — the panel above is already saying the subscriptions are on
        // their way, and "you don't have any yet" is the exact sentence this bug was about.
        awaitingCheckout ? null : (
          <CAlert color="info">
            You don't have any strategies yet.{" "}
            <Link to={routePaths.strategies}>Browse strategies</Link>
            .
          </CAlert>
        )
      ) : filtered.length === 0 && !showIncluded ? (
        awaitingCheckout ? null : (
          <CAlert
            color="info"
            className="d-flex justify-content-between align-items-center"
          >
            <span>
              No {statusFilter === "active" ? "active" : "inactive"}{" "}
              strategies.
            </span>
            <CButton
              color="info"
              variant="outline"
              size="sm"
              onClick={() => updateStatus("all")}
            >
              Show all
            </CButton>
          </CAlert>
        )
      ) : (
        <div className="d-flex flex-column gap-5">
          {filtered.length > 0 && (
            <div>
              {showIncluded && (
                <SectionHeading
                  title="Subscribed"
                  detail={strategyCount(filteredGroups.length)}
                />
              )}
              <CRow xs={{ gutter: 4 }}>
                {visibleGroups.map((group) => {
                  const activeRows = group.filter((s) => isActiveSubscription(s));
                  const alsoIn = membershipsIncludingAny(activeRows, membershipItems);
                  // Each side is its own script, so the card says "on its way" until every held side is invited.
                  const invites = activeRows.map(
                    (s) => invitedAsByStrategyId.get(s.strategy.id) ?? null,
                  );
                  const invitedAs =
                    holdsTradingViewScripts && activeRows.length > 0
                      ? invites.includes(null)
                        ? null
                        : invites[0]
                      : undefined;

                  return (
                    <CCol key={group[0].subscriptionExternalId} md={6} xl={4}>
                      <SubscriptionCard
                        subscriptions={group}
                        alsoIn={alsoIn}
                        tradingViewInvitedAs={invitedAs}
                      />
                    </CCol>
                  );
                })}
              </CRow>

            {showPagination && (
              <div className="d-flex flex-column align-items-center gap-2 mt-4">
                <small className="text-body-secondary">
                  Showing <strong>{startIdx + 1}</strong>–<strong>{endIdx}</strong> of{" "}
                  <strong>{filteredGroups.length}</strong>
                </small>
                <CPagination aria-label="Subscription pagination">
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    «
                  </CPaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNum) => (
                      <CPaginationItem
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => goToPage(pageNum)}
                      >
                        {pageNum}
                      </CPaginationItem>
                    ),
                  )}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    »
                  </CPaginationItem>
                </CPagination>
              </div>
            )}
            </div>
          )}

          {showIncluded && (
            <div>
              <SectionHeading
                title="From your memberships"
                detail={
                  selectedMembershipId === null
                    ? strategyCount(included.length)
                    : `${filteredIncluded.length} of ${included.length}`
                }
              >
                {membershipItems.length > 1 && (
                  <div className="d-flex gap-2 flex-wrap">
                    <FilterPill
                      label="All memberships"
                      count={included.length}
                      active={selectedMembershipId === null}
                      onClick={() => setMembershipFilter(null)}
                    />
                    {membershipItems.map((membership) => (
                      <FilterPill
                        key={membership.id}
                        label={membership.tierName}
                        count={
                          included.filter((i) =>
                            i.memberships.some((m) => m.id === membership.id),
                          ).length
                        }
                        active={selectedMembershipId === membership.id}
                        onClick={() => setMembershipFilter(membership.id)}
                        leading={<MembershipBadge membership={membership} size={16} />}
                      />
                    ))}
                  </div>
                )}
              </SectionHeading>
              <CRow xs={{ gutter: 4 }}>
                {visibleIncluded.map((entry) => (
                  <CCol key={entry.sides[0].id} md={6} xl={4}>
                    <IncludedStrategyCard
                      sides={entry.sides}
                      memberships={entry.memberships}
                    />
                  </CCol>
                ))}
              </CRow>
              {!showAllIncluded &&
                filteredIncluded.length > INCLUDED_PREVIEW_COUNT && (
                  <div className="d-flex justify-content-center mt-4">
                    <CButton
                      color="primary"
                      variant="outline"
                      onClick={() => setShowAllIncluded(true)}
                    >
                      Show all {filteredIncluded.length}
                    </CButton>
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      {/* These cards show a backtested curve against an active subscription period. Saying what
          the curve is, once, is what stops the two reading as the same thing. */}
      {!isLoading && !error && (filtered.length > 0 || showIncluded) && <BacktestNote />}
    </div>
  );
}

interface IncludedStrategy {
  /** The sides the memberships include, in side order. */
  sides: TradingStrategyDto[];
  memberships: CustomerMembershipDto[];
}

/**
 * Each strategy the customer's memberships include, once, with every membership that includes one of its
 * sides. A strategy they subscribe to a side of is left to its subscribed card, which is the one with
 * something to manage.
 */
function includedStrategies(
  subscriptions: CustomerTradingStrategySubscriptionDto[],
  memberships: CustomerMembershipDto[],
  strategies: TradingStrategyDto[],
): IncludedStrategy[] {
  const boughtStrategies = new Set(
    subscriptions
      .filter((subscription) => isActiveSubscription(subscription))
      .map((subscription) => sideGroupKey(subscription.strategy)),
  );
  const strategiesById = new Map(strategies.map((strategy) => [strategy.id, strategy]));
  const includedIds = new Set(memberships.flatMap((membership) => membership.tradingStrategyIds));

  const includedSides: TradingStrategyDto[] = [];
  for (const strategyId of includedIds) {
    const strategy = strategiesById.get(strategyId);
    if (!strategy || boughtStrategies.has(sideGroupKey(strategy))) {
      continue;
    }

    includedSides.push(strategy);
  }

  const included: IncludedStrategy[] = [];
  for (const sides of groupSides(includedSides)) {
    const including = memberships.filter((membership) =>
      sides.some((side) => membership.tradingStrategyIds.includes(side.id)),
    );
    included.push({ sides, memberships: including });
  }

  return included.sort((a, b) =>
    a.sides[0].publicName.localeCompare(b.sides[0].publicName),
  );
}

/**
 * One entry per strategy, holding its subscriptions in side order, live before ended. Entries keep the
 * order of their first subscription, so the most recently started strategy stays first.
 */
function groupBySubscribedStrategy(
  subscriptions: CustomerTradingStrategySubscriptionDto[],
): CustomerTradingStrategySubscriptionDto[][] {
  const groups = new Map<string, CustomerTradingStrategySubscriptionDto[]>();
  for (const subscription of subscriptions) {
    const key = sideGroupKey(subscription.strategy);
    const group = groups.get(key);
    if (group) {
      group.push(subscription);
    } else {
      groups.set(key, [subscription]);
    }
  }

  const result = [...groups.values()];
  for (const group of result) {
    group.sort((a, b) => {
      const sideOrder = bySide(a.strategy, b.strategy);
      if (sideOrder !== 0) {
        return sideOrder;
      }

      return Number(isActiveSubscription(b)) - Number(isActiveSubscription(a));
    });
  }
  return result;
}

function membershipsIncludingAny(
  subscriptions: CustomerTradingStrategySubscriptionDto[],
  memberships: CustomerMembershipDto[],
): CustomerMembershipDto[] {
  return memberships.filter((membership) =>
    subscriptions.some((subscription) => membership.tradingStrategyIds.includes(subscription.strategy.id)),
  );
}

function strategyCount(count: number): string {
  return `${count} ${count === 1 ? "strategy" : "strategies"}`;
}

function SectionHeading({
  title,
  detail,
  children,
}: {
  title: string;
  detail: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 mb-3 border-bottom">
      <h5 className="mb-0">
        {title}
        <small className="text-body-secondary fw-normal ms-2">{detail}</small>
      </h5>
      {children}
    </div>
  );
}

function CheckoutDeclinedPanel() {
  return (
    <CAlert color="danger">
      <strong>Your card was declined.</strong>
      <div>
        Nothing was charged for what it couldn't pay for, and that hasn't been
        added. Add it to your cart again and pay with another card.
      </div>
    </CAlert>
  );
}

// Shown only to someone arriving straight from a completed checkout whose rows haven't caught up
// yet. The point is that it never says "no subscriptions" to a customer who has just paid.
function CheckoutWaitPanel({ expired }: { expired: boolean }) {
  if (expired) {
    return (
      <CAlert color="warning">
        <strong>This is taking longer than usual.</strong>
        <div>
          Your new subscriptions haven't finished setting up. They should
          appear here shortly. If they're still missing in a few minutes,{" "}
          <Link to={routePaths.contact}>contact support</Link> and we'll sort it
          out.
        </div>
      </CAlert>
    );
  }

  return (
    <CAlert color="info" className="d-flex align-items-start gap-3">
      <CSpinner size="sm" className="mt-1 flex-shrink-0" />
      <div>
        <strong>We're setting up your new subscriptions.</strong>
        <div>
          Thanks for your payment. This usually takes a few seconds — they'll
          appear here on their own, no need to refresh.
        </div>
      </div>
    </CAlert>
  );
}

function FilterPill({
  label,
  count,
  active,
  onClick,
  leading,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  leading?: React.ReactNode;
}) {
  return (
    <CButton
      color={active ? "primary" : "secondary"}
      variant={active ? undefined : "outline"}
      size="sm"
      shape="rounded-pill"
      className="d-inline-flex align-items-center gap-1"
      onClick={onClick}
    >
      {leading}
      {label}
      <span className="ms-1 opacity-75">({count})</span>
    </CButton>
  );
}

interface SubscriptionCardProps {
  /** One strategy's subscriptions, a row each, in side order. The first is the one the card shows and opens. */
  subscriptions: CustomerTradingStrategySubscriptionDto[];
  /** The memberships that also include this strategy. */
  alsoIn: CustomerMembershipDto[];
  /**
   * The TradingView username this strategy's script is invited for, or null while the invite is on its way.
   * Left out where TradingView invites aren't the subject.
   */
  tradingViewInvitedAs?: string | null;
}

export function SubscriptionCard({ subscriptions, alsoIn, tradingViewInvitedAs }: SubscriptionCardProps) {
  const { t } = useTranslation("tradingStrategies");
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const strategy = subscriptions[0].strategy;
  const detailPath = `/subscriptions/${strategy.id}`;

  const cardStyle: React.CSSProperties = {
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    transform: hovered ? "translateY(-2px)" : "none",
    boxShadow: hovered ? "0 0.5rem 1rem rgba(0,0,0,0.08)" : undefined,
  };

  const handleActivate = () => navigate(detailPath);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleActivate();
    }
  };

  return (
    <CCard
      className="h-100"
      style={cardStyle}
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`View subscription for ${strategy.publicName}`}
    >
      <StrategyIdentityBand strategy={strategy} />
      <CCardBody className="d-flex flex-column">
        <CCardTitle>{strategy.publicName}</CCardTitle>
        <CCardText
          className="text-body-secondary"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {strategy.description}
        </CCardText>
        <div className="mt-auto">
          <StrategyCurve strategy={strategy} emphasised={hovered} />
          {alsoIn.length > 0 && (
            <div className="d-flex align-items-center gap-2 small mt-2 px-2 py-1 rounded border bg-body-tertiary">
              <MembershipBadgeStack memberships={alsoIn} />
              <span className="text-truncate">
                {alsoIn.length === 1
                  ? `Also in your ${alsoIn[0].tierName} membership`
                  : `Also in ${formatMembershipNames(alsoIn)}`}
              </span>
            </div>
          )}
          {tradingViewInvitedAs === null && (
            <div className="d-flex align-items-center gap-2 small mt-2 px-2 py-1 rounded border bg-body-tertiary">
              <TradingViewMark />
              <span aria-hidden className="rounded-circle flex-shrink-0 bg-warning" style={{ width: 8, height: 8 }} />
              <span className="text-truncate">TradingView invite on its way</span>
            </div>
          )}
          {tradingViewInvitedAs && (
            // The account is in the tooltip so a long username can't stretch the card; the strategy's page shows it in full.
            <CTooltip content={`Invited as ${tradingViewInvitedAs}`}>
              <div className="d-flex align-items-center gap-2 small mt-2 px-2 py-1 rounded border bg-body-tertiary">
                <TradingViewMark />
                <span aria-hidden className="rounded-circle flex-shrink-0 bg-success" style={{ width: 8, height: 8 }} />
                <span className="text-truncate">Invited to TradingView</span>
              </div>
            </CTooltip>
          )}
        </div>
      </CCardBody>
      <CCardFooter className="bg-body-tertiary">
        {/* A row per held side with its own status, since two sides held at once can differ. Each
            row opens that side's subscription; price and dates are there and in Billing. */}
        <div className="strategy-sides">
          <div className="strategy-sides-head" style={{ gridTemplateColumns: HELD_SIDE_COLUMNS }}>
            <span>{t("card.sides.side")}</span>
            <span className="text-end">{t("card.sides.status")}</span>
            <span />
          </div>
          {subscriptions.map((subscription) => (
            <HeldSideRow key={subscription.subscriptionExternalId} subscription={subscription} />
          ))}
        </div>
      </CCardFooter>
    </CCard>
  );
}

const HELD_SIDE_COLUMNS = "minmax(0, 1fr) auto 1rem";

/** The one date that goes with a held side's status: when it started, or when access stops or stopped. */
function heldSideDate(
  subscription: CustomerTradingStrategySubscriptionDto,
  displayStatus: SubscriptionDisplayStatus,
): string | null {
  if (displayStatus === "Active") {
    return `since ${formatDateShort(subscription.activeFrom)}`;
  }
  if (!subscription.activeTo) {
    return null;
  }
  if (displayStatus === "Cancelled") {
    return `until ${formatDateShort(subscription.activeTo)}`;
  }
  return `ended ${formatDateShort(subscription.activeTo)}`;
}

function HeldSideRow({ subscription }: { subscription: CustomerTradingStrategySubscriptionDto }) {
  const displayStatus = deriveSubscriptionDisplayStatus(subscription);
  const date = heldSideDate(subscription, displayStatus);
  const strategy = subscription.strategy;

  // Inside a card that is itself a link, so the row's click and Enter stop here rather than also
  // opening the card's first side.
  return (
    <Link
      to={`/subscriptions/${strategy.id}`}
      className="strategy-side-row"
      style={{ gridTemplateColumns: HELD_SIDE_COLUMNS }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {strategy.direction ? (
        <StrategySideLabel direction={strategy.direction} className="fw-medium" />
      ) : (
        <span className="text-truncate">{strategy.publicName}</span>
      )}
      <span className="d-flex align-items-center justify-content-end gap-2">
        {date && <small className="held-side-date text-body-secondary text-nowrap">{date}</small>}
        <CBadge
          color={subscriptionStatusColor(displayStatus)}
          shape="rounded-pill"
          className="d-flex align-items-center gap-1"
        >
          {displayStatus === "Active" && <CIcon icon={cilCheckCircle} size="sm" />}
          {displayStatus}
        </CBadge>
      </span>
      <CIcon icon={cilChevronRight} className="card-link-chevron" aria-hidden />
    </Link>
  );
}
